import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { CASE_STUDY_BY_SLUG, CASE_STUDIES } from "@/data/caseStudies";
import PageHead from "@/components/PageHead";
import CTAStrip from "@/components/CTAStrip";
import { Reveal } from "@/components/Reveal";
import { ArrowUpRight } from "@phosphor-icons/react";

export default function CaseStudyPage() {
  const { slug } = useParams();
  const c = CASE_STUDY_BY_SLUG[slug];
  if (!c) return <Navigate to="/case-studies" replace />;

  const others = CASE_STUDIES.filter((x) => x.slug !== c.slug).slice(0, 3);

  return (
    <main data-testid={`case-study-page-${c.slug}`}>
      <PageHead
        title={c.title}
        description={c.summary}
        path={`/case-studies/${c.slug}`}
        image={c.cover}
        type="article"
      />

      <section className="border-b border-citirex-border bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-12 md:px-12 md:pb-24 md:pt-20">
          <Reveal>
            <div className="flex items-center gap-3">
              <Link
                to="/case-studies"
                className="font-body text-xs uppercase tracking-[0.25em] text-citirex-sub hover:text-citirex-ink"
              >
                Case studies
              </Link>
              <span className="font-body text-xs text-citirex-sub">/</span>
              <span className="font-body text-xs uppercase tracking-[0.25em] text-citirex-accent">
                {c.vertical}
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-8 max-w-5xl font-display text-4xl font-semibold leading-[1] tracking-tight md:text-6xl lg:text-7xl">
              {c.title}
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-3xl font-editorial text-2xl italic leading-snug text-citirex-ink md:text-3xl">
              {c.summary}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 font-body text-sm uppercase tracking-[0.2em] text-citirex-sub">
              Partner · {c.partner}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="relative aspect-[21/9] w-full overflow-hidden">
            <img
              src={c.cover}
              alt={c.title}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="bg-citirex-ink text-citirex-inv">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-12 md:py-24">
          <p className="overline">Outcomes</p>
          <div className="mt-10 grid grid-cols-1 gap-px bg-white/10 md:grid-cols-3">
            {c.metrics.map((m) => (
              <div key={m.l} className="bg-citirex-ink p-8">
                <p className="font-editorial text-6xl font-medium leading-none md:text-7xl">
                  {m.v}
                </p>
                <p className="mt-6 font-body text-xs uppercase tracking-[0.2em] text-citirex-inv/70">
                  {m.l}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-citirex-bg">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-24 md:grid-cols-12 md:gap-16 md:px-12 md:py-32">
          <aside className="md:col-span-3">
            <div className="sticky top-28">
              <p className="overline">In this study</p>
              <ol className="mt-6 space-y-2 border-t border-citirex-border pt-6">
                {c.sections.map((s, i) => (
                  <li
                    key={s.h}
                    className="flex items-baseline gap-3 font-body text-sm text-citirex-ink"
                  >
                    <span className="font-editorial italic text-citirex-sub">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {s.h}
                  </li>
                ))}
              </ol>
            </div>
          </aside>
          <article className="md:col-span-9">
            <div className="border-t border-citirex-border">
              {c.sections.map((s, i) => (
                <div
                  key={s.h}
                  className="border-b border-citirex-border py-10"
                >
                  <p className="font-editorial text-3xl italic text-citirex-accent">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h2 className="mt-3 font-display text-3xl font-medium text-citirex-ink md:text-4xl">
                    {s.h}
                  </h2>
                  <p className="mt-4 font-body text-base leading-relaxed text-citirex-sub md:text-lg">
                    {s.p}
                  </p>
                </div>
              ))}
            </div>
          </article>
        </div>
      </section>

      <section className="border-t border-citirex-border bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <p className="overline">More case studies</p>
          <div className="mt-12 grid grid-cols-1 gap-px bg-citirex-border md:grid-cols-3">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/case-studies/${o.slug}`}
                data-testid={`case-study-other-${o.slug}`}
                className="group flex flex-col bg-citirex-bg p-8 transition-colors hover:bg-citirex-surface"
              >
                <div className="flex items-center justify-between">
                  <span className="font-body text-[10px] uppercase tracking-[0.25em] text-citirex-sub">
                    {o.vertical}
                  </span>
                  <ArrowUpRight
                    size={18}
                    weight="bold"
                    className="transition-transform group-hover:rotate-45"
                  />
                </div>
                <h3 className="mt-8 font-display text-xl font-medium text-citirex-ink md:text-2xl">
                  {o.title}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTAStrip
        eyebrow="Ready when you are"
        title={`Run the ${c.vertical.toLowerCase()} playbook with Citirex.`}
        buttonText="Talk to sales"
      />
    </main>
  );
}
